import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalFooter, ModalOverlay, ModalHeader } from "@chakra-ui/modal";
import { updateOrganization, createOrganization, getOrganization } from "api/graphQL_organizations";
import DivLoader from "components/divloader/DivLoader";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { MdCheck, MdEdit, MdFileCopy, MdHighlightOff, MdInfo } from "react-icons/md";
import { BsPlusCircle } from "react-icons/bs";
import { MultiSelect } from "react-multi-select-component";
import _ from "lodash";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

export interface iCreateOrgForm {
  name: string;
  description: string;
  website: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  payment_methods: string[];
  id?: string;
}

/* ── Step config ── */
const STEPS = [
  { key: "identity",  label: "Identity",  subtitle: "Name & description",   icon: "building" },
  { key: "contact",   label: "Contact",   subtitle: "Email, phone & web",   icon: "contact" },
  { key: "address",   label: "Address",   subtitle: "Location details",     icon: "location" },
  { key: "gateways",  label: "Gateways",  subtitle: "Assign gateways",      icon: "gateway" },
];

const StepSVG = ({ icon, active, done }: { icon: string; active: boolean; done: boolean }) => {
  const col = done ? "#fff" : active ? "#fff" : "#94a3b8";
  const icons: Record<string, JSX.Element> = {
    building: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    contact: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    location: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    gateway: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  };
  return icons[icon] ?? null;
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ErrorMsg = ({ msg }: { msg?: string }) => (
  <p className={`owz-err${msg ? "" : " owz-err--hidden"}`} aria-live="polite">
    {msg && (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    )}
    {msg ?? ""}
  </p>
);

const CreateOrgModal = ({
  fetchOrganizations,
  fetchAllOrganizations,
  allGateways,
  id,
  info,
  roleData,
  infoData,
}: {
  fetchOrganizations: () => void;
  fetchAllOrganizations: () => void;
  allGateways?: any;
  id?: string;
  info?: boolean;
  roleData?: any;
  infoData?: any;
}) => {
  const rootForm: iCreateOrgForm = {
    name: "",
    description: "",
    website: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    payment_methods: [] as string[],
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<iCreateOrgForm>(rootForm);
  const [initialValues, setInitialFormValues] = React.useState<iCreateOrgForm>(rootForm);
  const [viewMode, setViewMode] = React.useState<boolean>(info);
  const [selected, setSelected] = React.useState([]);
  const [options, setGatewayOption] = React.useState([]);
  const [formValuesErr, setFormValuesErr] = React.useState<any>({});
  const [multiselectError, setMultiselectError] = React.useState<string>("");
  const [methodName, setMethodName] = React.useState<boolean>(false);
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [isFetchingOrg, setIsFetchingOrg] = React.useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [popoverOpenE, setPopoverOpenE] = React.useState(false);

  /* wizard state */
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [animDir, setAnimDir] = useState<"forward" | "back">("forward");
  const [animating, setAnimating] = useState(false);

  const totalSteps = STEPS.length;

  React.useEffect(() => {
    if (allGateways && allGateways.length > 0) {
      const method2 = roleData?.[0]?.organization?.value?.organization_show_method_name;
      setMethodName(method2);
      const newOptions = allGateways.map((ele: any) => ({
        value: ele.id,
        label: method2 ? ele.name : ele.id,
      }));
      setGatewayOption(newOptions);
    }
  }, [allGateways]);

  const handleClose = () => {
    setIsCloseModal(true);
    setTimeout(() => {
      setIsCloseModal(false);
      onClose();
      setFormValues(rootForm);
      setFormValuesErr({});
      setMultiselectError("");
      setSelected([]);
      setCurrentStep(0);
      setCompletedSteps(new Set());
      setIsFetchingOrg(false);
      setViewMode(info);
      setPopoverOpen(false);
      setPopoverOpenE(false);
    }, 320);
  };

  const websiteRx = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d{1,5})?(\/.*)?$/;
  const emailRx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateStep = (step: number): boolean => {
    let errs: any = {};
    if (step === 0) {
      if (!formValues.name?.trim()) errs.name = "Organization name is required";
      if (!formValues.description?.trim()) errs.description = "Description is required";
      if (!formValues.website?.trim()) errs.website = "Website is required";
      else if (formValues.website && !websiteRx.test(formValues.website)) errs.website = "Invalid website URL";
    }
    if (step === 1) {
      if (!formValues.email?.trim()) errs.email = "Email is required";
      else if (!emailRx.test(formValues.email)) errs.email = "Invalid email address";
      if (!formValues.phone?.trim()) errs.phone = "Phone is required";
    }
    if (step === 2) {
      if (!formValues.address?.trim()) errs.address = "Address is required";
      if (!formValues.city?.trim()) errs.city = "City is required";
      if (!formValues.state?.trim()) errs.state = "State is required";
      if (!formValues.country?.trim()) errs.country = "Country is required";
      if (!formValues.postal_code?.trim()) errs.postal_code = "Postal code is required";
    }
    if (step === 3) {
      if (!formValues.payment_methods || formValues.payment_methods.length === 0) {
        setMultiselectError("Please select at least one gateway");
        return false;
      } else {
        setMultiselectError("");
      }
    }
    setFormValuesErr((prev: any) => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const animateToStep = (next: number) => {
    if (animating) return;
    const dir = next > currentStep ? "forward" : "back";
    setAnimDir(dir);
    setAnimating(true);
    setTimeout(() => { setCurrentStep(next); setAnimating(false); }, 280);
  };

  const handleNext = () => {
    if (viewMode) { animateToStep(currentStep + 1); return; }
    if (!validateStep(currentStep)) return;
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    animateToStep(currentStep + 1);
  };

  const handleBack = () => { animateToStep(currentStep - 1); };

  const handleStepClick = (idx: number) => {
    if (viewMode || idx < currentStep || completedSteps.has(idx)) animateToStep(idx);
  };

  function handleValueChange(e: any) {
    const { id: fid, value } = e.target;
    let err = "";
    if (fid === "website" && value && !websiteRx.test(value)) err = "Invalid website URL";
    if (fid === "email" && value && !emailRx.test(value)) err = "Invalid email address";
    setFormValuesErr((p: any) => ({ ...p, [fid]: err }));
    setFormValues((p: any) => ({ ...p, [fid]: value }));
  }

  const handleMultiSelectChange = (sel: any) => {
    setSelected(sel);
    setFormValues((prev: any) => ({
      ...prev,
      payment_methods: sel?.map((ele: any) => ele.value),
    }));
    if (sel?.length > 0) setMultiselectError("");
    else setMultiselectError("Please select at least one gateway");
  };

  const handlePhoneChange = (value: any) => {
    setFormValues((prev) => ({ ...prev, phone: value }));
    if (value) setFormValuesErr((prev: any) => ({ ...prev, phone: "" }));
    else setFormValuesErr((prev: any) => ({ ...prev, phone: "Phone is required" }));
  };

  const hasFormChanged = () =>
    Object.keys(formValues).some(
      (key) => formValues[key as keyof iCreateOrgForm] !== initialValues[key as keyof iCreateOrgForm]
    );

  async function fetchOrganizationData(orgId: string) {
    setIsFetchingOrg(true);
    await getOrganization({ id: orgId })
      .then((data: any) => {
        setFormValues(data[0]);
        setInitialFormValues(data[0]);
        const showMethodName = roleData?.[0]?.organization?.value?.organization_show_method_name;
        const selectedOptions = data[0].payment_methods?.map((pmId: string) => {
          const gateway = allGateways?.find((item: any) => item.id === pmId);
          return { value: pmId, label: showMethodName ? gateway?.name : pmId };
        });
        setSelected(selectedOptions ?? []);
        if (data[0].payment_methods?.length > 0) setMultiselectError("");
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message ?? "Unable to fetch data");
      })
      .finally(() => setIsFetchingOrg(false));
  }

  async function handleSubmit() {
    if (!validateStep(3)) return;
    try {
      setIsLoading(true);
      if (id) {
        const hasChanges = !_.isEqual(formValues, initialValues);
        if (!hasChanges) {
          toast.error("No changes detected. You're all set!");
          handleClose();
          return;
        }
        const update = await updateOrganization(formValues);
        if (update.errors) {
          toast.error(update.errors[0].message);
        } else if (update.data?.[0]?.status === "Success") {
          toast.success("Organization updated successfully");
          fetchOrganizations();
          fetchAllOrganizations();
          handleClose();
        } else {
          toast.error(update.data?.[0]?.message ?? "Something went wrong!");
        }
      } else {
        const create = await createOrganization(formValues);
        if (create.errors) {
          toast.error(create.errors[0].message);
        } else if (create[0]?.status === "Success") {
          toast.success("Organization created successfully");
          fetchOrganizations();
          fetchAllOrganizations();
          handleClose();
        } else {
          toast.error(create[0]?.message ?? "Something went wrong!");
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Unable to save organization");
    } finally {
      setIsLoading(false);
    }
  }

  /* ── Step content rendered as inline JSX (no sub-components to avoid remount/focus loss) ── */
  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="owz-step-body">
            <div className="owz-step-intro">
              <div className="owz-step-intro-icon owz-step-intro-icon--blue">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <div>
                <h3 className="owz-step-heading">Organization Identity</h3>
                <p className="owz-step-desc">Set the organization's name and description.</p>
              </div>
            </div>
            <div className="owz-fields">
              <div className="owz-field-full owz-field-wrap">
                <label className="owz-label">Organization Name <span className="owz-req">*</span></label>
                <input
                  type="text"
                  id="name"
                  className={`owz-input${formValuesErr?.name ? " owz-input--err" : ""}`}
                  placeholder="e.g. Google Inc."
                  disabled={viewMode}
                  value={formValues?.name ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, name: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, name: "" }));
                  }}
                />
                {formValuesErr?.name && (
                  <ErrorMsg msg={formValuesErr?.name} />
                )}
              </div>
              <div className="owz-field-full owz-field-wrap">
                <label className="owz-label">Description <span className="owz-req">*</span></label>
                <textarea
                  rows={4}
                  id="description"
                  className={`owz-textarea${formValuesErr?.description ? " owz-textarea--err" : ""}`}
                  placeholder="Brief description of this organization…"
                  disabled={viewMode}
                  value={formValues?.description ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, description: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, description: "" }));
                  }}
                />
                {formValuesErr?.description && (
                  <ErrorMsg msg={formValuesErr?.description} />
                )}
              </div>
              <div className="owz-field-full owz-field-wrap">
                <label className="owz-label">Website <span className="owz-req">*</span></label>
                <div className="owz-input-icon-wrap">
                  <span className="owz-input-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    id="website"
                    className={`owz-input owz-input--icon${formValuesErr?.website ? " owz-input--err" : ""}`}
                    placeholder="www.company.com"
                    disabled={viewMode}
                    value={formValues?.website ?? ""}
                    onChange={handleValueChange}
                  />
                </div>
                {formValuesErr?.website && (
                  <ErrorMsg msg={formValuesErr?.website} />
                )}
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="owz-step-body">
            <div className="owz-step-intro">
              <div className="owz-step-intro-icon owz-step-intro-icon--green">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div>
                <h3 className="owz-step-heading">Contact Details</h3>
                <p className="owz-step-desc">How to reach this organization.</p>
              </div>
            </div>
            <div className="owz-fields">
              <div className="owz-field-half owz-field-wrap">
                <label className="owz-label">Email <span className="owz-req">*</span></label>
                <div className="owz-input-icon-wrap">
                  <span className="owz-input-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    id="email"
                    className={`owz-input owz-input--icon${formValuesErr?.email ? " owz-input--err" : ""}`}
                    placeholder="contact@company.com"
                    disabled={viewMode}
                    value={formValues?.email ?? ""}
                    onChange={handleValueChange}
                  />
                </div>
                {formValuesErr?.email && (
                  <ErrorMsg msg={formValuesErr?.email} />
                )}
              </div>
              <div className="owz-field-half owz-field-wrap">
                <label className="owz-label-phone">Phone <span className="owz-req">*</span></label>
                <PhoneInput
                  country=""
                  disabled={viewMode}
                  value={formValues?.phone ?? ""}
                  onChange={handlePhoneChange}
                  inputStyle={{
                    width: "100%", height: "46px",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: "14px", 
                    border: `${formValuesErr?.phone ? "1.5px solid #ef4444" : "1.5px solid #e2e8f0"}`,
                    borderRadius: "10px",
                    background: viewMode ? "#f8fafc" : "#f8fafc",
                    color: "#0f172a"
                  }}
                  buttonStyle={{
                    borderRadius: "10px 0 0 10px",
                    background: "#f8fafc",
                    border: `${formValuesErr?.phone ? "1.5px solid #ef4444" : "1.5px solid #e2e8f0"}`
                  }}
                  containerStyle={{ 
                    width: "100%",
                  }}
                />
                {formValuesErr?.phone && (
                  <ErrorMsg msg={formValuesErr?.phone} />
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="owz-step-body">
            <div className="owz-step-intro">
              <div className="owz-step-intro-icon owz-step-intro-icon--purple">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <div>
                <h3 className="owz-step-heading">Address</h3>
                <p className="owz-step-desc">Physical location of this organization.</p>
              </div>
            </div>
            <div className="owz-fields">
              <div className="owz-field-full owz-field-wrap">
                <label className="owz-label">Address Line <span className="owz-req">*</span></label>
                <input
                  type="text"
                  id="address"
                  className={`owz-input${formValuesErr?.address ? " owz-input--err" : ""}`}
                  placeholder="45 Test Street"
                  disabled={viewMode}
                  value={formValues?.address ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, address: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, address: "" }));
                  }}
                />
                {formValuesErr?.address && (
                  <ErrorMsg msg={formValuesErr?.address} />
                )}
              </div>
              <div className="owz-field-half owz-field-wrap">
                <label className="owz-label">City <span className="owz-req">*</span></label>
                <input
                  type="text"
                  id="city"
                  className={`owz-input${formValuesErr?.city ? " owz-input--err" : ""}`}
                  placeholder="Mountain View"
                  disabled={viewMode}
                  value={formValues?.city ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, city: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, city: "" }));
                  }}
                />
                {formValuesErr?.city && (
                  <ErrorMsg msg={formValuesErr?.city} />
                )}
              </div>
              <div className="owz-field-half owz-field-wrap">
                <label className="owz-label">State <span className="owz-req">*</span></label>
                <input
                  type="text"
                  id="state"
                  className={`owz-input${formValuesErr?.state ? " owz-input--err" : ""}`}
                  placeholder="California"
                  disabled={viewMode}
                  value={formValues?.state ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, state: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, state: "" }));
                  }}
                />
                {formValuesErr?.state && (
                  <ErrorMsg msg={formValuesErr?.state} />
                )}
              </div>
              <div className="owz-field-half owz-field-wrap">
                <label className="owz-label">Country <span className="owz-req">*</span></label>
                <input
                  type="text"
                  id="country"
                  className={`owz-input${formValuesErr?.country ? " owz-input--err" : ""}`}
                  placeholder="USA"
                  disabled={viewMode}
                  value={formValues?.country ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, country: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, country: "" }));
                  }}
                />
                {formValuesErr?.country && (
                  <ErrorMsg msg={formValuesErr?.country} />
                )}
              </div>
              <div className="owz-field-half owz-field-wrap">
                <label className="owz-label">Postal Code <span className="owz-req">*</span></label>
                <input
                  type="text"
                  id="postal_code"
                  className={`owz-input${formValuesErr?.postal_code ? " owz-input--err" : ""}`}
                  placeholder="94043"
                  disabled={viewMode}
                  value={formValues?.postal_code ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, postal_code: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, postal_code: "" }));
                  }}
                />
                {formValuesErr?.postal_code && (
                  <ErrorMsg msg={formValuesErr?.postal_code} />
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="owz-step-body">
            <div className="owz-step-intro">
              <div className="owz-step-intro-icon owz-step-intro-icon--amber">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <div>
                <h3 className="owz-step-heading">Payment Gateways</h3>
                <p className="owz-step-desc">Select at least one gateway for this organization.</p>
              </div>
            </div>
            <div className="owz-fields">
              <div className="owz-field-full owz-field-wrap">
                <label className="owz-label">Gateways <span className="owz-req">*</span></label>
                <div className={`owz-multiselect-wrap${multiselectError ? " owz-multiselect-wrap--err" : ""}`}>
                  <MultiSelect
                    options={options}
                    value={selected}
                    onChange={handleMultiSelectChange}
                    labelledBy="Select"
                    isCreatable={false}
                    disabled={viewMode}
                    overrideStrings={{ noOptions: "No gateways found!" }}
                    valueRenderer={(sel, opts) => {
                      if (sel.length === 0) return "Select gateway(s)…";
                      if (sel.length === opts.length) return "All gateways selected";
                      return sel.map((o) => o.label).join(", ");
                    }}
                  />
                </div>
                {multiselectError && (
                  <ErrorMsg msg={multiselectError} />
                )}

                {/* {selected.length > 0 && (
                  <div className="owz-gw-preview">
                    <p className="owz-gw-preview-label">Selected gateways:</p>
                    <div className="owz-gw-chips">
                      {(selected as any[]).map((s: any) => (
                        <span key={s.value} className="owz-gw-chip">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="4" width="22" height="16" rx="2"/>
                            <line x1="1" y1="10" x2="23" y2="10"/>
                          </svg>
                          {s.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === totalSteps - 1;
  const progressPct = (currentStep / (totalSteps - 1)) * 100;

  /* ── Trigger element ── */
  const TriggerEl = () => {
    if (info) {
      if (!roleData?.[0]?.organization?.value?.view_organization) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
          <PopoverTrigger>
            <button
              onClick={() => {
                if (id) fetchOrganizationData(id);
                setViewMode(true);
                setCurrentStep(0);
                onOpen();
              }}
              onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}
            >
              <div className="org-action-icon org-action-icon--blue">
                <MdInfo size={16} />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="200px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">
              View Organization
            </PopoverHeader>
            <PopoverArrow bg="#eff6ff" />
          </PopoverContent>
        </Popover>
      );
    }
    if (id) {
      if (!roleData?.[0]?.organization?.value?.edit_organization) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpenE && !isOpen} onOpen={() => setPopoverOpenE(true)} onClose={() => setPopoverOpenE(false)}>
          <PopoverTrigger>
            <button
              onClick={() => {
                if (id) fetchOrganizationData(id);
                setViewMode(false);
                setCurrentStep(0);
                onOpen();
              }}
              onMouseEnter={() => !isOpen && setPopoverOpenE(true)} onMouseLeave={() => setPopoverOpenE(false)}
            >
              <div className="org-action-icon org-action-icon--amber">
                <MdEdit size={16} />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #fde68a" borderRadius="12px" bg="#fffbeb" maxW="200px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#fef3c7" fontSize="12px" fontWeight="700" color="#92400e" borderBottom="1px solid #fde68a" px="12px" py="9px">
              Edit Organization
            </PopoverHeader>
            <PopoverArrow bg="#fffbeb" />
          </PopoverContent>
        </Popover>
      );
    }
    if (!roleData?.[0]?.organization?.value?.add_organization) return null;
    return (
      <button
        className="org-add-btn"
        onClick={() => {
          setViewMode(false);
          setCurrentStep(0);
          setCompletedSteps(new Set());
          onOpen();
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="16"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
        Add New Organization
      </button>
    );
  };

  return (
    <>
      <TriggerEl />

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div className={`owz-panel${isOpen && !isCloseModal ? " owz-panel--open" : " owz-panel--close"}`}>

          {/* Decorative top gradient bar */}
          <div className="owz-top-glow" />

          {/* Header */}
          <div className="owz-header">
            <div className="owz-header-brand">
              <div className="owz-header-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {viewMode ? (
                    <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#fff"/></>
                  ) : id ? (
                    <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                  ) : (
                    <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>
                  )}
                </svg>
              </div>
              <div>
                <h2 className="owz-header-title">
                  {viewMode
                    ? "Organization Details"
                    : id
                    ? `Edit — ${infoData?.row?.original?.name ?? "Organization"}`
                    : "New Organization"}
                </h2>
                <p className="owz-header-sub">
                  Step {currentStep + 1} of {totalSteps} · {STEPS[currentStep].label}
                </p>
              </div>
            </div>
            <div className="owz-header-right">
              {viewMode && roleData?.[0]?.organization?.value?.edit_organization && (
                <button className="owz-edit-btn" onClick={() => setViewMode(false)}>
                  <MdEdit size={13} /> Switch to Edit
                </button>
              )}
              <button className="owz-close-btn" onClick={handleClose} aria-label="Close">
                <MdHighlightOff size={22} />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="owz-progress-track">
            <div className="owz-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          {/* Step indicators */}
          <div className="owz-steps-row">
            {STEPS.map((step, i) => {
              const isActive = i === currentStep;
              const isDone = completedSteps.has(i);
              const isReach = viewMode || i <= currentStep || completedSteps.has(i);
              return (
                <div key={step.key} className="owz-step-item" onClick={() => isReach && handleStepClick(i)}>
                  <div className={`owz-step-circle${isActive ? " owz-step-circle--active" : isDone ? " owz-step-circle--done" : " owz-step-circle--idle"}`}>
                    {isDone && !isActive ? <CheckIcon /> : <StepSVG icon={step.icon} active={isActive} done={isDone} />}
                    {isActive && <div className="owz-step-pulse" />}
                  </div>
                  <div className="owz-step-label-block">
                    <span className={`owz-step-label${isActive ? " owz-step-label--active" : isDone ? " owz-step-label--done" : ""}`}>
                      {step.label}
                    </span>
                    <span className="owz-step-sublabel">{step.subtitle}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`owz-step-connector${isDone ? " owz-step-connector--done" : ""}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="owz-content-area">
            {isFetchingOrg ? (
              <div className="owz-skeleton-wrap">
                <div className="owz-skeleton-intro">
                  <div className="owz-skeleton owz-skeleton--icon" />
                  <div className="owz-skeleton-intro-text">
                    <div className="owz-skeleton owz-skeleton--title" />
                    <div className="owz-skeleton owz-skeleton--subtitle" />
                  </div>
                </div>
                <div className="owz-skeleton-fields">
                  {[1, 2].map((n) => (
                    <div key={n} className="owz-skeleton-field">
                      <div className="owz-skeleton owz-skeleton--label" />
                      <div className="owz-skeleton owz-skeleton--input" />
                    </div>
                  ))}
                  <div className="owz-skeleton-field owz-skeleton-field--full">
                    <div className="owz-skeleton owz-skeleton--label" />
                    <div className="owz-skeleton owz-skeleton--textarea" />
                  </div>
                </div>
              </div>
            ) : (
              <div className={`owz-anim-wrap${animating ? ` owz-anim--exit-${animDir}` : ` owz-anim--enter-${animDir}`}`}>
                {renderStep(currentStep)}
              </div>
            )}
          </div>

          {/* Footer nav */}
          <div className="owz-footer">
            <div className="owz-footer-left">
              <div className="owz-dot-row">
                {STEPS.map((_, i) => (
                  <div key={i} className={`owz-dot${i === currentStep ? " owz-dot--active" : completedSteps.has(i) ? " owz-dot--done" : ""}`} />
                ))}
              </div>
              <span className="owz-footer-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Encrypted &amp; secure
              </span>
            </div>
            <div className="owz-footer-btns">
              {currentStep > 0 && (
                <button className="owz-btn-back" onClick={handleBack}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back
                </button>
              )}
              <button className="owz-btn-cancel" onClick={handleClose}>Cancel</button>

              {isLastStep ? (
                !viewMode && (
                  <button
                    className="owz-btn-submit"
                    onClick={handleSubmit}
                    disabled={isLoading || (!!id && !hasFormChanged())}
                  >
                    {isLoading ? (
                      <span className="owz-spinner" />
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {id ? "Update Organization" : "Create Organization"}
                      </>
                    )}
                  </button>
                )
              ) : (
                <button className="owz-btn-next" onClick={handleNext} disabled={isFetchingOrg}>
                  {viewMode ? "Next" : "Continue"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* ── Add button ── */
        .org-add-btn {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          cursor: pointer; box-shadow: 0 3px 12px rgba(37,99,235,.3);
          transition: transform .15s, box-shadow .18s;
        }
        .org-add-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37,99,235,.38); }

        /* ── Action icons ── */
        .org-action-icon {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all .15s; border: 1.5px solid;
        }
        .org-action-icon--blue { background: #eff6ff; border-color: #bfdbfe; color: #2563eb; }
        .org-action-icon--blue:hover { background: #dbeafe; border-color: #93c5fd; }
        .org-action-icon--amber { background: #fffbeb; border-color: #fde68a; color: #d97706; }
        .org-action-icon--amber:hover { background: #fef3c7; border-color: #fcd34d; }

        /* ── Slide panel ── */
        .owz-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 900px;
          background: #ffffff;
          z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -12px 0 60px rgba(15,23,42,.18);
          transition: transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow: hidden;
        }
        .owz-panel--open { transform: translateX(0); }
        .owz-panel--close { transform: translateX(100%); }

        /* Top glow */
        .owz-top-glow {
          height: 3px;
          background: linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744);
          background-size: 200% 100%;
          animation: owzGlow 3s linear infinite; flex-shrink: 0;
        }
        @keyframes owzGlow { 0%{background-position:0%} 100%{background-position:200%} }

        /* Header */
        .owz-header {
          padding: 15px 26px;
          background: linear-gradient(135deg,#0f2744 0%,#1a3d6b 60%,#1d4ed8 100%);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .owz-header-brand { display: flex; align-items: center; gap: 13px; }
        .owz-header-logo {
          width: 42px; height: 42px; border-radius: 12px;
          background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.25);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .owz-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 1; }
        .owz-header-sub { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 500; }
        .owz-header-right { display: flex; align-items: center; gap: 10px; }
        .owz-edit-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 700; color: #fff;
          background: rgba(255,255,255,.14); border: 1.5px solid rgba(255,255,255,.25);
          border-radius: 8px; padding: 7px 12px; cursor: pointer; transition: background .15s;
        }
        .owz-edit-btn:hover { background: rgba(255,255,255,.24); }
        .owz-close-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,.65); display: flex; align-items: center; padding: 2px; transition: color .15s;
        }
        .owz-close-btn:hover { color: #fff; }

        /* Progress bar */
        .owz-progress-track { height: 4px; background: #e8edf3; flex-shrink: 0; }
        .owz-progress-fill { height: 100%; background: linear-gradient(90deg,#1d4ed8,#7c3aed); border-radius: 0 4px 4px 0; transition: width .4s cubic-bezier(.4,0,.2,1); }

        /* Steps row */
        .owz-steps-row {
          display: flex; align-items: flex-start;
          padding: 16px 22px 12px;
          background: #fafbfc; border-bottom: 1px solid #f1f5f9;
          flex-shrink: 0; overflow-x: auto; gap: 0;
        }
        .owz-step-item { display: flex; align-items: center; flex: 1; min-width: 0; cursor: default; }

        .owz-step-circle {
          width: 36px; height: 36px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; position: relative; transition: all .25s;
        }
        .owz-step-circle--idle   { background: #f1f5f9; border: 2px solid #e2e8f0; }
        .owz-step-circle--active { background: linear-gradient(135deg,#1d4ed8,#2563eb); border: 2px solid transparent; box-shadow: 0 0 0 4px rgba(37,99,235,.15); }
        .owz-step-circle--done   { background: linear-gradient(135deg,#15803d,#16a34a); border: 2px solid transparent; box-shadow: 0 0 0 3px rgba(22,163,74,.12); }

        .owz-step-pulse { position: absolute; inset: -4px; border-radius: 50%; border: 2px solid rgba(37,99,235,.35); animation: owzPulse 1.8s ease-in-out infinite; }
        @keyframes owzPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0;transform:scale(1.4)} }

        .owz-step-label-block { display: flex; flex-direction: column; gap: 1px; padding: 0 6px; min-width: 0; }
        .owz-step-label { font-size: 11.5px; font-weight: 600; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .owz-step-label--active { color: #1d4ed8; font-weight: 800; }
        .owz-step-label--done { color: #15803d; font-weight: 700; }
        .owz-step-sublabel { font-size: 10px; color: #cbd5e1; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .owz-step-connector { flex: 1; height: 2px; background: #e2e8f0; margin: 0 6px; border-radius: 2px; transition: background .3s; }
        .owz-step-connector--done { background: linear-gradient(90deg,#16a34a,#22c55e); }

        /* Content area */
        .owz-content-area { flex: 1; overflow-y: auto; overflow-x: hidden; position: relative; }
        .owz-content-area::-webkit-scrollbar { width: 4px; }
        .owz-content-area::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        /* Animations */
        .owz-anim-wrap { transition: opacity .25s ease, transform .28s cubic-bezier(0.16,1,0.3,1); }
        .owz-anim--enter-forward { animation: owzSlideInRight .28s cubic-bezier(0.16,1,0.3,1) both; }
        .owz-anim--enter-back    { animation: owzSlideInLeft  .28s cubic-bezier(0.16,1,0.3,1) both; }
        .owz-anim--exit-forward  { animation: owzSlideOutLeft .18s ease both; }
        .owz-anim--exit-back     { animation: owzSlideOutRight .18s ease both; }
        @keyframes owzSlideInRight  { from{opacity:0;transform:translateX(32px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes owzSlideInLeft   { from{opacity:0;transform:translateX(-32px)} to{opacity:1;transform:translateX(0)} }
        @keyframes owzSlideOutLeft  { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(-24px)} }
        @keyframes owzSlideOutRight { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(24px)} }

        /* Step body */
        .owz-step-body { padding: 24px 28px 8px; }
        .owz-step-intro { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
        .owz-step-intro-icon { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .owz-step-intro-icon--blue   { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .owz-step-intro-icon--green  { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; color: #16a34a; }
        .owz-step-intro-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .owz-step-intro-icon--amber  { background: linear-gradient(135deg,#fffbeb,#fef3c7); border: 1.5px solid #fde68a; color: #d97706; }
        .owz-step-heading { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -.3px; margin-bottom: 3px; line-height: 22px; }
        .owz-step-desc { font-size: 13px; color: #64748b; font-weight: 500; line-height: 1.5; }

        /* Form fields */
        .owz-fields { display: grid; grid-template-columns: 1fr 1fr; column-gap: 16px; row-gap: 8px; align-items: start; }
        .owz-field-full  { grid-column: 1/-1; }
        .owz-field-half  { grid-column: span 1; }
        .owz-field-wrap  { display: flex; flex-direction: column; }

        .owz-label { font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 6px; display: block; }
        .owz-label-phone { font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 0px; display: block; }
        .owz-req   { color: #dc2626; }
        .owz-opt   { font-weight: 500; color: #94a3b8; font-size: 11.5px; }

        .owz-input {
          width: 100%; padding: 12px 14px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a;
          outline: none; transition: border-color .18s, box-shadow .18s, background .18s;
          box-sizing: border-box;
        }
        .owz-input::placeholder { color: #cbd5e1; }
        .owz-input:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
        .owz-input--err { border-color: #ef4444 !important; background: #fff7f7 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.08) !important; }
        .owz-input:disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
        .owz-input--icon { padding-left: 40px; }

        .owz-input-icon-wrap { position: relative; }
        .owz-input-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #94a3b8; display: flex; align-items: center; pointer-events: none; }

        .owz-textarea {
          width: 100%; padding: 12px 14px; min-height: 90px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a;
          outline: none; resize: vertical;
          transition: border-color .18s, box-shadow .18s;
          box-sizing: border-box;
        }
        .owz-textarea::placeholder { color: #cbd5e1; }
        .owz-textarea:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
        .owz-textarea--err { border-color: #ef4444 !important; background: #fff7f7 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.08) !important; }
        .owz-textarea:disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }

        /* Multiselect — table-layout trick prevents width growth on multi-select */
        .owz-multiselect-wrap {
          display: table !important;
          table-layout: fixed !important;
          width: 100% !important;
          min-width: 0 !important;
          position: relative;
          border-radius: 11px;
          overflow: visible;
        }
        .owz-multiselect-wrap .dropdown-container {
          display: block !important;
          width: 100% !important; max-width: 100% !important; min-width: 0 !important;
          box-sizing: border-box !important;
          border: 1.5px solid #e2e8f0 !important; border-radius: 11px !important;
          background: #f8fafc !important;
          font-family: 'Plus Jakarta Sans', sans-serif !important;
          overflow: visible !important;
        }
        .owz-multiselect-wrap .dropdown-container:focus-within {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37,99,235,.1) !important;
          background: #fff !important;
        }
        .owz-multiselect-wrap--err .dropdown-container {
          border-color: #ef4444 !important; background: #fff7f7 !important;
        }
        .owz-multiselect-wrap .dropdown-heading {
          display: flex !important; align-items: center !important;
          height: 46px !important; min-height: 46px !important; max-height: 46px !important;
          padding: 0 12px !important; overflow: hidden !important;
          width: 100% !important; max-width: 100% !important; box-sizing: border-box !important;
        }
        .owz-multiselect-wrap .dropdown-heading-value {
          flex: 1 1 0% !important; min-width: 0 !important;
          max-width: 100% !important; overflow: hidden !important;
        }
        .owz-multiselect-wrap .dropdown-heading-value span,
        .owz-multiselect-wrap .dropdown-heading-value > * {
          display: block !important; overflow: hidden !important;
          text-overflow: ellipsis !important; white-space: nowrap !important;
          width: 100% !important; font-size: 13.5px !important; color: #0f172a !important;
        }
        .owz-multiselect-wrap .dropdown-content {
          z-index: 9999 !important; border: 1.5px solid #e2e8f0 !important;
          border-radius: 10px !important; box-shadow: 0 8px 24px rgba(15,23,42,0.12) !important;
          overflow: visible !important; position: absolute !important; width: 100% !important;
        }
        .owz-multiselect-wrap .options {
          max-height: 220px !important; overflow-y: auto !important;
          border-radius: 0 0 10px 10px !important;
        }
        .owz-multiselect-wrap .options li label { font-size: 13px !important; color: #374151; }
        .owz-multiselect-wrap input[type="text"] { font-size: 13px !important; }
        .dropdown-content { z-index: 9999 !important; }
        @media (max-width: 480px) {
          .owz-multiselect-wrap .dropdown-heading {
            height: 42px !important; min-height: 42px !important; max-height: 42px !important;
          }
          .owz-multiselect-wrap .dropdown-heading-value span,
          .owz-multiselect-wrap .dropdown-heading-value > * { font-size: 12.5px !important; }
        }

        /* Gateway preview chips */
        .owz-gw-preview { margin-top: 12px; }
        .owz-gw-preview-label { font-size: 12px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 8px; }
        .owz-gw-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .owz-gw-chip {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 12px; font-weight: 600;
          padding: 5px 10px; border-radius: 7px;
          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          border: 1.5px solid #bfdbfe; color: #1d4ed8;
        }

        /* Error */
        .owz-err { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #dc2626; font-weight: 500; margin-top: 5px; min-height: 21px; line-height: 1.4; }
        .owz-err--hidden { visibility: hidden; }

        /* Footer */
        .owz-footer {
          padding: 14px 26px;
          border-top: 1.5px solid #f1f5f9;
          background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; flex-shrink: 0;
        }
        .owz-footer-left { display: flex; align-items: center; gap: 16px; }
        .owz-dot-row { display: flex; gap: 6px; align-items: center; }
        .owz-dot { width: 7px; height: 7px; border-radius: 50%; background: #e2e8f0; transition: all .2s; }
        .owz-dot--active { width: 20px; border-radius: 4px; background: linear-gradient(90deg,#1d4ed8,#2563eb); }
        .owz-dot--done { background: #16a34a; }
        .owz-footer-hint { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .owz-footer-btns { display: flex; align-items: center; gap: 8px; }

        .owz-btn-back {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .owz-btn-back:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }

        .owz-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .owz-btn-cancel:hover { background: #f1f5f9; color: #374151; }

        .owz-btn-next {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#1d4ed8,#2563eb);
          cursor: pointer; box-shadow: 0 4px 14px rgba(37,99,235,.3);
          transition: transform .15s, box-shadow .18s; position: relative; overflow: hidden;
        }
        .owz-btn-next::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg,rgba(255,255,255,.1),transparent 60%); }
        .owz-btn-next:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,.4); }
        .owz-btn-next:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        .owz-btn-submit {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#15803d,#16a34a);
          cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,.3);
          transition: transform .15s, box-shadow .18s, opacity .18s; position: relative; overflow: hidden;
        }
        .owz-btn-submit::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg,rgba(255,255,255,.12),transparent 60%); }
        .owz-btn-submit:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(22,163,74,.4); }
        .owz-btn-submit:disabled { opacity: .5; cursor: not-allowed; transform: none; }

        .owz-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: owzSpin .65s linear infinite; }
        @keyframes owzSpin { to{transform:rotate(360deg)} }

        /* Skeleton */
        .owz-skeleton-wrap { padding: 28px 28px 8px; }
        .owz-skeleton-intro { display: flex; align-items: center; gap: 14px; margin-bottom: 28px; }
        .owz-skeleton-intro-text { flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .owz-skeleton-fields { display: flex; flex-direction: column; gap: 20px; }
        .owz-skeleton-field { display: flex; flex-direction: column; gap: 8px; }
        .owz-skeleton-field--full { width: 100%; }
        .owz-skeleton { border-radius: 8px; background: linear-gradient(90deg,#f1f5f9 25%,#e8edf5 50%,#f1f5f9 75%); background-size: 400% 100%; animation: owzShimmer 1.5s ease-in-out infinite; }
        @keyframes owzShimmer { 0%{background-position:100% 0} 100%{background-position:-100% 0} }
        .owz-skeleton--icon     { width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0; }
        .owz-skeleton--title    { height: 22px; width: 180px; border-radius: 6px; }
        .owz-skeleton--subtitle { height: 14px; width: 280px; border-radius: 6px; }
        .owz-skeleton--label    { height: 13px; width: 120px; border-radius: 4px; }
        .owz-skeleton--input    { height: 46px; width: 100%; border-radius: 11px; }
        .owz-skeleton--textarea { height: 88px; width: 100%; border-radius: 11px; }

        @media (max-width: 640px) {
          .owz-panel { max-width: 100%; }
          .owz-header { padding: 16px 18px; }
          .owz-steps-row { padding: 12px 14px 10px; }
          .owz-step-label-block { display: none; }
          .owz-step-body { padding: 20px 18px 8px; }
          .owz-footer { padding: 12px 18px; }
          .owz-fields { grid-template-columns: 1fr; }
          .owz-field-half { grid-column: span 1; }
        }
      `}</style>
    </>
  );
};

export default CreateOrgModal;

/* ── TruncateCopy helper (kept for backward compat) ── */
export const TruncateCopy = ({
  info,
  slice = 15,
  showCopy = true,
}: {
  info: any;
  slice?: number;
  showCopy?: boolean;
}) => {
  const [copy, setCopy] = React.useState<boolean>(false);
  return (
    <div className="flex items-center gap-2">
      <p className="overflow-auto text-sm font-bold text-navy-700 dark:text-white">
        {info?.getValue()}
      </p>
      {showCopy && (
        <>
          {copy ? (
            <MdCheck className="cursor-pointer text-teal-500" />
          ) : (
            <MdFileCopy
              className="cursor-pointer text-teal-500"
              onClick={() => {
                navigator.clipboard.writeText(info?.getValue());
                setCopy(true);
                setTimeout(() => setCopy(false), 1500);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};